/**
 * Value Realization Chart - Cumulative Value Timeline
 *
 * Displays a line chart showing:
 * - Cumulative value realized over time (weeks)
 * - Milestones when value starts accruing
 * - Current progress marker
 *
 * Design: Clean SVG line chart with gradient fill
 */

import React, { useMemo } from 'react';
import type { Task, ValueRealizationSchedule, ActionPlanSummary } from '../types/actionPlan';
import { formatCurrency, scheduleKindOf } from './actionPlanValueFormat';
import { hasFiniteValue } from './actionPlanMatrixPlacement';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ValueRealizationChartProps {
  schedule: ValueRealizationSchedule;
  summary: ActionPlanSummary;
  /**
   * The rendered tasks (S4f-C) — for the exclusion disclosure (the server's
   * schedule builder already contributes NOTHING for unpriced tasks, so the
   * curve must say which tasks it does not describe) and for resolving each
   * milestone's contributing tasks to titles.
   */
  tasks: Task[];
  selectedTaskId?: string | null;
  /** Selecting a milestone selects its contributing task — canonical selection. */
  onTaskClick?: (taskId: string) => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// The third copy of `formatCurrency` in the Action Plan tree, all three of which
// returned '$0' for an absent value. One shared implementation now (P0-4).

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ValueRealizationChart: React.FC<ValueRealizationChartProps> = ({
  schedule,
  tasks,
  selectedTaskId = null,
  onTaskClick,
}) => {
  // Chart dimensions (always defined, hooks must be called unconditionally)
  const width = 400;
  const height = 200;
  const padding = { top: 20, right: 20, bottom: 30, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  /*
   * FINITE, FAIL-CLOSED COORDINATES (S4h). The previous mapping was
   * `Number(t.week) || 0` / `Number(t.cumulative_value_usd) || 0`: a malformed
   * point — NaN, Infinity, a runtime string, a null — was silently DRAWN AT
   * ZERO, which fabricates a dip in a forecast curve. A point now plots only
   * when BOTH coordinates are genuine finite numbers; everything else is
   * excluded from the series and DISCLOSED next to the chart. A genuine zero
   * is a real coordinate and stays on the curve (`0` weeks, `$0` cumulative —
   * both are measurements, not absences). No `|| 1` stand-ins either: a plan
   * whose curve is legitimately flat at zero renders flat at zero.
   */
  const { xScale, yScale, maxWeek, maxValue, pathData, plotted, malformedPointCount } =
    useMemo(() => {
      const empty = {
        xScale: (_week: number) => 0,
        yScale: (_value: number) => chartHeight,
        maxWeek: 0,
        maxValue: 0,
        pathData: '',
        plotted: [] as NonNullable<typeof schedule>['timeline'],
        malformedPointCount: 0,
      };
      if (!schedule?.timeline || schedule.timeline.length === 0) return empty;

      const isFinitePoint = (t: (typeof schedule.timeline)[number]): boolean =>
        typeof t.week === 'number' &&
        Number.isFinite(t.week) &&
        typeof t.cumulative_value_usd === 'number' &&
        Number.isFinite(t.cumulative_value_usd);

      const plotted = schedule.timeline.filter(isFinitePoint);
      const malformedPointCount = schedule.timeline.length - plotted.length;
      if (plotted.length === 0) return { ...empty, malformedPointCount };

      const maxWeek = Math.max(...plotted.map((t) => t.week));
      const maxValue = Math.max(...plotted.map((t) => t.cumulative_value_usd));

      const xScale = (week: number) =>
        maxWeek > 0 ? Math.min(Math.max((week / maxWeek) * chartWidth, 0), chartWidth) : 0;
      const yScale = (value: number) =>
        maxValue > 0
          ? Math.min(Math.max(chartHeight - (value / maxValue) * chartHeight, 0), chartHeight)
          : chartHeight;

      const points = plotted.map((t) => ({
        x: xScale(t.week),
        y: yScale(t.cumulative_value_usd),
      }));

      let pathData = '';
      if (points.length > 0) {
        pathData = `M ${points[0].x} ${points[0].y}`;
        for (let i = 1; i < points.length; i++) {
          const prevPoint = points[i - 1];
          const currPoint = points[i];
          const midX = (prevPoint.x + currPoint.x) / 2;
          pathData += ` Q ${prevPoint.x} ${prevPoint.y}, ${midX} ${(prevPoint.y + currPoint.y) / 2}`;
          pathData += ` Q ${currPoint.x} ${currPoint.y}, ${currPoint.x} ${currPoint.y}`;
        }
      }

      return { xScale, yScale, maxWeek, maxValue, pathData, plotted, malformedPointCount };
    }, [schedule, chartWidth, chartHeight]);

  // Y-axis ticks
  const yTicks = useMemo(() => {
    if (maxValue === 0) return [];
    const tickCount = 5;
    const ticks = [];
    for (let i = 0; i <= tickCount; i++) {
      const value = (maxValue / tickCount) * i;
      const y = yScale(value);
      if (isFinite(y)) {
        ticks.push({ value, y });
      }
    }
    return ticks;
  }, [maxValue, yScale]);

  // X-axis ticks (every 4 weeks)
  const xTicks = useMemo(() => {
    if (maxWeek === 0) return [];
    const ticks = [];
    const interval = Math.max(Math.ceil(maxWeek / 8), 1); // Ensure at least 1
    for (let week = 0; week <= maxWeek && ticks.length < 20; week += interval) { // Limit to 20 ticks max
      const x = xScale(week);
      if (isFinite(x)) {
        ticks.push({ week, x });
      }
    }
    return ticks;
  }, [maxWeek, xScale]);

  // Handle missing schedule data (early return AFTER all hooks)
  /*
   * EXCLUSION DISCLOSURE (S4f-C). The server's schedule builder adds nothing for
   * an unpriced task (S4d: `if (hasValue(task.value_usd))`), and a task without a
   * usable effort estimate has no schedule position at all. The curve therefore
   * describes ONLY the priced, schedulable tasks — and says so, with the two
   * exclusion reasons stated separately, rather than drawing a line that reads
   * as the whole plan.
   */
  const unpricedCount = tasks.filter((t) => !hasFiniteValue(t.value_usd)).length;
  const pricedUnscheduledCount = tasks.filter(
    (t) => hasFiniteValue(t.value_usd) && scheduleKindOf(t.effort_weeks) === 'unavailable',
  ).length;
  const contributingCount = tasks.length - unpricedCount - pricedUnscheduledCount;

  if (!schedule) {
    return (
      <div className="value-realization-chart">
        <h3>Value Curve — cumulative forecast</h3>
        <div className="chart-empty">
          <p>No forecast schedule available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="value-realization-chart">
      <h3>Value Curve — cumulative forecast</h3>
      {tasks.length > 0 && (
        <p className="ap-vc-exclusions" data-testid="ap-vc-exclusions">
          Drawn from {contributingCount} priced, schedulable task{contributingCount === 1 ? '' : 's'}.
          {unpricedCount > 0 && (
            <> Excluded: {unpricedCount} unpriced (value input needed)</>
          )}
          {pricedUnscheduledCount > 0 && (
            <>{unpricedCount > 0 ? ';' : ' Excluded:'} {pricedUnscheduledCount} priced but without
            a usable effort estimate (cannot be scheduled)</>
          )}
          {(unpricedCount > 0 || pricedUnscheduledCount > 0) && <>. They are not drawn as zero.</>}
        </p>
      )}

      {/* Malformed timeline points (S4h): excluded from the series, never drawn
          at zero — and disclosed, because a curve silently missing points reads
          as if it covered them. */}
      {malformedPointCount > 0 && (
        <p className="ap-vc-exclusions" data-testid="ap-vc-malformed">
          {malformedPointCount} timeline point{malformedPointCount === 1 ? '' : 's'} could not be
          read (non-numeric week or value) and {malformedPointCount === 1 ? 'is' : 'are'} not
          drawn. Unreadable points are never drawn as zero.
        </p>
      )}

      {schedule.timeline && schedule.timeline.length > 0 ? (
        <>
          <svg width={width} height={height} className="chart-svg">
            <defs>
              {/* Gradient fill */}
              <linearGradient id="valueGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#4299e1" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#4299e1" stopOpacity="0.05" />
              </linearGradient>
            </defs>

            <g transform={`translate(${padding.left}, ${padding.top})`}>
              {/* Y-axis grid lines */}
              {yTicks.map((tick, i) => (
                <g key={i}>
                  <line
                    x1={0}
                    y1={tick.y}
                    x2={chartWidth}
                    y2={tick.y}
                    stroke="#e2e8f0"
                    strokeWidth="1"
                  />
                  <text
                    x={-10}
                    y={tick.y + 4}
                    textAnchor="end"
                    fontSize="10"
                    fill="#718096"
                  >
                    {formatCurrency(tick.value)}
                  </text>
                </g>
              ))}

              {/* X-axis grid lines */}
              {xTicks.map((tick, i) => (
                <g key={i}>
                  <line
                    x1={tick.x}
                    y1={0}
                    x2={tick.x}
                    y2={chartHeight}
                    stroke="#e2e8f0"
                    strokeWidth="1"
                    strokeDasharray="3,3"
                  />
                  <text
                    x={tick.x}
                    y={chartHeight + 15}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#718096"
                  >
                    W{tick.week}
                  </text>
                </g>
              ))}

              {/* Area fill */}
              {pathData && (
                <path
                  d={`${pathData} L ${chartWidth} ${chartHeight} L 0 ${chartHeight} Z`}
                  fill="url(#valueGradient)"
                />
              )}

              {/* Line */}
              {pathData && (
                <path
                  d={pathData}
                  fill="none"
                  stroke="#4299e1"
                  strokeWidth="2"
                />
              )}

              {/* Milestone markers — from the PLOTTED points only: a marker for
                  a malformed point would sit at a fabricated coordinate. */}
              {plotted
                .filter(t => t.milestones && t.milestones.length > 0)
                .map((milestone, i) => (
                  <g key={i}>
                    <circle
                      cx={xScale(milestone.week)}
                      cy={yScale(milestone.cumulative_value_usd)}
                      r="4"
                      fill="#4299e1"
                      stroke="white"
                      strokeWidth="2"
                    />
                  </g>
                ))}
            </g>
          </svg>

          {/* ONE series, named for what it is (S4f-C): the chart draws a single
              cumulative FORECAST line. The old legend claimed two series
              ("Value Realized" / "Projected Value") for one line — nothing here
              measures realisation (P1-7). */}
          <div className="chart-legend">
            <div className="legend-item">
              <div className="legend-marker projected"></div>
              <span>Cumulative forecast (priced tasks only)</span>
            </div>
          </div>

          {/* Key Milestones — task-linked (S4f-C): selecting a milestone selects
              its contributing task, which every other view then opens on. A
              milestone whose contributing tasks cannot be resolved stays a
              non-interactive row rather than a dead button. */}
          {schedule.timeline.some(t => t.milestones && t.milestones.length > 0) && (
            <div className="milestones-list">
              <h4>Key Milestones</h4>
              {schedule.timeline
                .filter(t => t.milestones && t.milestones.length > 0)
                .slice(0, 3)
                .map((item, i) => {
                  const contributing = (item.tasks_completed ?? [])
                    .map((id) => tasks.find((t) => t.id === id))
                    .filter((t): t is Task => Boolean(t));
                  const selectable = onTaskClick && contributing.length > 0;
                  const isSelected =
                    selectedTaskId !== null &&
                    contributing.some((t) => t.id === selectedTaskId);
                  const body = (
                    <>
                      <span className="milestone-week">Week {item.week}</span>
                      <span className="milestone-desc">{item.milestones[0]}</span>
                      {contributing.length > 0 && (
                        <span className="milestone-tasks">
                          {contributing.map((t) => t.title).join(' · ')}
                        </span>
                      )}
                    </>
                  );
                  return selectable ? (
                    <button
                      key={i}
                      type="button"
                      className={`milestone-item milestone-item--selectable${isSelected ? ' is-selected' : ''}`}
                      aria-current={isSelected ? 'true' : undefined}
                      onClick={() => onTaskClick(contributing[0].id)}
                    >
                      {body}
                    </button>
                  ) : (
                    <div key={i} className="milestone-item">
                      {body}
                    </div>
                  );
                })}
            </div>
          )}
        </>
      ) : (
        <div className="chart-empty">
          <p>No value realization data available</p>
        </div>
      )}
    </div>
  );
};
